import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Mail, Phone, BadgeCheck, Clock, CalendarDays, Coffee, Plane, Stethoscope, Car, MapPin, Flag } from "lucide-react";
import DayNav, { dateKey } from "@/components/DayNav";
import { deriveShiftsFromTrips, fmtDurationMins } from "@/lib/worktimeFromTrips";

const fmt = (mins) => {
  if (mins == null) return "—";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}h ${m}m`;
};
const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString("de-DE") : "—");
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("de-DE") : "—");

export default function DriverDetailModal({ driver, open, onOpenChange }) {
  const [worktimes, setWorktimes] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [summary, setSummary] = useState({ daily_minutes: 0, weekly_minutes: 0, monthly_minutes: 0 });
  const [trips, setTrips] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [statsPeriod, setStatsPeriod] = useState("year"); // month | year | all

  useEffect(() => {
    if (!driver || !open) return;
    setSelectedDate(new Date());
    (async () => {
      try {
        const [w, l, s, t] = await Promise.all([
          api.get("/worktimes", { params: { driver_id: driver.id } }),
          api.get("/leaves", { params: { driver_id: driver.id } }),
          api.get("/worktimes/summary", { params: { driver_id: driver.id } }),
          api.get("/trips", { params: { driver_id: driver.id } }),
        ]);
        setWorktimes(w.data);
        setLeaves(l.data);
        setSummary(s.data);
        setTrips(t.data);
      } catch (e) { if (e?.response?.status !== 401) toast.error(formatApiError(e)); }
    })();
  }, [driver, open]);

  const derivedShifts = useMemo(() => deriveShiftsFromTrips(trips), [trips]);
  const dayShift = useMemo(() => {
    const key = dateKey(selectedDate);
    return derivedShifts.find((s) => s.date === key) || null;
  }, [derivedShifts, selectedDate]);
  const dayTrips = useMemo(() => {
    const key = dateKey(selectedDate);
    return trips
      .filter((t) => t.trip_date === key)
      .sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));
  }, [trips, selectedDate]);

  const dayLeaves = useMemo(() => {
    const key = dateKey(selectedDate);
    return leaves.filter((l) => l.date_from <= key && key <= l.date_to);
  }, [leaves, selectedDate]);

  // Stats for the donut chart: Arbeitstage / Urlaub / Krank + Fahrten (per selected period).
  const stats = useMemo(() => {
    const todayKey = dateKey(new Date());
    const now = new Date();
    let pStart = null;
    let pEnd = null;
    if (statsPeriod === "month") {
      pStart = dateKey(new Date(now.getFullYear(), now.getMonth(), 1));
      pEnd = dateKey(new Date(now.getFullYear(), now.getMonth() + 1, 0));
    } else if (statsPeriod === "year") {
      pStart = `${now.getFullYear()}-01-01`;
      pEnd = `${now.getFullYear()}-12-31`;
    }
    const inPeriod = (key) => (!pStart || key >= pStart) && (!pEnd || key <= pEnd);

    const counted = trips.filter((t) =>
      t.status !== "storniert" &&
      (t.status === "abgeschlossen" || (t.trip_date && t.trip_date < todayKey)) &&
      t.trip_date && inPeriod(t.trip_date)
    );
    const uSet = new Set();
    const kSet = new Set();
    for (const l of leaves) {
      const end = new Date(l.date_to + "T00:00:00");
      let d = new Date(l.date_from + "T00:00:00");
      let guard = 0;
      while (d <= end && guard < 2000) {
        const key = dateKey(d);
        if (inPeriod(key)) (l.type === "urlaub" ? uSet : kSet).add(key);
        d.setDate(d.getDate() + 1);
        guard++;
      }
    }
    const aSet = new Set();
    for (const t of counted) {
      if (t.trip_date && !uSet.has(t.trip_date) && !kSet.has(t.trip_date)) aSet.add(t.trip_date);
    }
    return { arbeitDays: aSet.size, urlaubDays: uSet.size, krankDays: kSet.size, tripCount: counted.length };
  }, [trips, leaves, statsPeriod]);

  const donutBg = useMemo(() => {
    const { arbeitDays: a, urlaubDays: u, krankDays: k } = stats;
    const total = a + u + k;
    if (!total) return "conic-gradient(hsl(var(--muted)) 0deg 360deg)";
    let acc = 0;
    const seg = (v, c) => { const s = (acc / total) * 360; acc += v; const e = (acc / total) * 360; return `${c} ${s}deg ${e}deg`; };
    return `conic-gradient(${seg(a, "#3b82f6")}, ${seg(u, "#38bdf8")}, ${seg(k, "#fb923c")})`;
  }, [stats]);

  if (!driver) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle data-testid="driver-detail-title">{driver.firstname} {driver.lastname}</DialogTitle>
          <DialogDescription>Übersicht der Stammdaten, Arbeitszeiten und Abwesenheiten.</DialogDescription>
        </DialogHeader>

        {/* Profile + Übersichtsdiagramm */}
        <Card className="p-4">
          <div className="flex flex-col sm:flex-row gap-6 items-center sm:items-start">
            {/* Donut: Arbeit / Urlaub / Krank */}
            <div className="flex flex-col items-center gap-3 shrink-0" data-testid="driver-stats-chart">
              <div className="inline-flex rounded-md border border-border overflow-hidden text-xs" data-testid="driver-stats-period">
                {[
                  { v: "month", l: "Monat" },
                  { v: "year", l: "Jahr" },
                  { v: "all", l: "Gesamt" },
                ].map((o) => (
                  <button
                    key={o.v}
                    onClick={() => setStatsPeriod(o.v)}
                    className={`px-2.5 py-1 transition-colors ${statsPeriod === o.v ? "bg-blue-600 text-white" : "bg-transparent text-muted-foreground hover:bg-muted"}`}
                    data-testid={`driver-stats-period-${o.v}`}
                  >
                    {o.l}
                  </button>
                ))}
              </div>
              <div className="relative h-32 w-32 rounded-full" style={{ background: donutBg }} role="img" aria-label={`Verteilung: ${stats.arbeitDays} Arbeitstage, ${stats.urlaubDays} Urlaubstage, ${stats.krankDays} Kranktage, ${stats.tripCount} Fahrten`}>
                <div className="absolute inset-[16px] rounded-full bg-card flex flex-col items-center justify-center shadow-inner">
                  <div className="text-2xl heading font-semibold tabular-nums leading-none" data-testid="driver-stats-arbeitstage">{stats.arbeitDays}</div>
                  <div className="text-[10px] uppercase text-muted-foreground tracking-wider mt-0.5">Arbeitstage</div>
                </div>
              </div>
              <div className="text-xs space-y-1 w-full">
                <div className="flex items-center gap-1.5" data-testid="driver-stats-legend-arbeit">
                  <span className="h-2.5 w-2.5 rounded-sm bg-blue-500 shrink-0" />
                  <span className="text-muted-foreground">Arbeit:</span>
                  <span className="font-semibold tabular-nums">{stats.arbeitDays} Tage</span>
                  <span className="text-muted-foreground">· {stats.tripCount} Fahrten</span>
                </div>
                <div className="flex items-center gap-1.5" data-testid="driver-stats-legend-urlaub">
                  <span className="h-2.5 w-2.5 rounded-sm bg-sky-400 shrink-0" />
                  <span className="text-muted-foreground">Urlaub:</span>
                  <span className="font-semibold tabular-nums">{stats.urlaubDays} Tage</span>
                </div>
                <div className="flex items-center gap-1.5" data-testid="driver-stats-legend-krank">
                  <span className="h-2.5 w-2.5 rounded-sm bg-orange-400 shrink-0" />
                  <span className="text-muted-foreground">Krank:</span>
                  <span className="font-semibold tabular-nums">{stats.krankDays} Tage</span>
                </div>
              </div>
            </div>

            {/* Stammdaten (umsortiert) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 text-sm flex-1 w-full">
              <div>
                <div className="text-xs uppercase text-muted-foreground tracking-wider">Personalnr.</div>
                <div className="font-mono tabular-nums flex items-center gap-1"><BadgeCheck className="h-3.5 w-3.5 text-muted-foreground" />{driver.employee_number}</div>
              </div>
              <div>
                <div className="text-xs uppercase text-muted-foreground tracking-wider">Status</div>
                <div className={driver.active ? "text-emerald-600 font-medium" : "text-red-600 font-medium"}>{driver.active ? "Aktiv" : "Inaktiv"}</div>
              </div>
              <div>
                <div className="text-xs uppercase text-muted-foreground tracking-wider">Telefon</div>
                <div className="flex items-center gap-1"><Phone className="h-3.5 w-3.5 text-muted-foreground" />{driver.phone || "—"}</div>
              </div>
              <div>
                <div className="text-xs uppercase text-muted-foreground tracking-wider">E-Mail</div>
                <div className="flex items-center gap-1 truncate"><Mail className="h-3.5 w-3.5 text-muted-foreground" />{driver.email || "—"}</div>
              </div>
            </div>
          </div>
        </Card>

        {/* KPIs */}
        <div className="grid grid-cols-3 gap-3">
          <Card className="p-3">
            <div className="text-xs uppercase text-muted-foreground tracking-wider flex items-center gap-1"><Clock className="h-3 w-3" /> Heute</div>
            <div className="text-xl heading font-semibold tabular-nums">{fmt(summary.daily_minutes)}</div>
          </Card>
          <Card className="p-3">
            <div className="text-xs uppercase text-muted-foreground tracking-wider flex items-center gap-1"><CalendarDays className="h-3 w-3" /> Woche</div>
            <div className="text-xl heading font-semibold tabular-nums">{fmt(summary.weekly_minutes)}</div>
          </Card>
          <Card className="p-3">
            <div className="text-xs uppercase text-muted-foreground tracking-wider flex items-center gap-1"><CalendarDays className="h-3 w-3" /> Monat</div>
            <div className="text-xl heading font-semibold tabular-nums">{fmt(summary.monthly_minutes)}</div>
          </Card>
        </div>

        {/* Tagesübersicht mit Navigation */}
        <Card className="p-4 space-y-3" data-testid="driver-day-overview">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-sm font-semibold heading">Tagesübersicht</div>
            <DayNav date={selectedDate} onChange={setSelectedDate} testid="driver-daynav" />
          </div>
          {dayLeaves.map((l) => (
            <div key={l.id} className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold mr-2 ${l.type === "urlaub" ? "bg-sky-100 text-sky-700 dark:bg-sky-900/60 dark:text-sky-200" : "bg-orange-100 text-orange-700 dark:bg-orange-900/60 dark:text-orange-200"}`} data-testid={`driver-day-leave-${l.id}`}>
              {l.type === "urlaub" ? <Plane className="h-3 w-3" /> : <Stethoscope className="h-3 w-3" />}
              {l.type === "urlaub" ? "Urlaub" : "Krank"} ({fmtDate(l.date_from)}–{fmtDate(l.date_to)})
            </div>
          ))}
          {dayTrips.length > 0 && (
            <div className="rounded-md border border-border overflow-x-auto">
              <div className="px-3 py-1.5 text-xs uppercase tracking-wider text-muted-foreground bg-muted/40">Fahrten am {fmtDate(selectedDate.toISOString())}</div>
              <table className="w-full text-sm min-w-[560px]">
                <thead className="bg-muted/30 text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="text-left px-3 py-1.5">Start</th>
                    <th className="text-left px-3 py-1.5">Ende</th>
                    <th className="text-left px-3 py-1.5">Abholung</th>
                    <th className="text-left px-3 py-1.5">Ziel</th>
                    <th className="text-left px-3 py-1.5">Fahrzeug</th>
                  </tr>
                </thead>
                <tbody>
                  {dayTrips.map((t) => {
                    const pk = (t.pickups && t.pickups[0]) || t.pickup_address;
                    const dt = (t.destinations && t.destinations[0]) || t.destination_address;
                    return (
                      <tr key={t.id} className="border-t border-border" data-testid={`driver-day-trip-${t.id}`}>
                        <td className="px-3 py-1.5 tabular-nums">{t.start_time}</td>
                        <td className="px-3 py-1.5 tabular-nums">{t.end_time || "—"}</td>
                        <td className="px-3 py-1.5 truncate max-w-[180px]"><span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3 text-emerald-600 shrink-0" />{pk ? `${pk.street || ""} ${pk.house_number || ""}${pk.city ? ", " + pk.city : ""}`.trim() || "—" : "—"}</span></td>
                        <td className="px-3 py-1.5 truncate max-w-[180px]"><span className="inline-flex items-center gap-1"><Flag className="h-3 w-3 text-blue-600 shrink-0" />{dt ? `${dt.street || ""} ${dt.house_number || ""}${dt.city ? ", " + dt.city : ""}`.trim() || "—" : "—"}</span></td>
                        <td className="px-3 py-1.5 text-muted-foreground">{t.license_plate || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {dayLeaves.length === 0 && dayTrips.length === 0 && (
            <div className="text-sm text-muted-foreground italic" data-testid="driver-day-empty">Keine Einträge an diesem Tag.</div>
          )}
        </Card>

        {/* Leaves */}
        {leaves.length > 0 && (
          <Card className="overflow-x-auto">
            <div className="px-4 py-2 border-b border-border text-sm font-semibold heading">Abwesenheiten</div>
            <table className="w-full text-sm">
              <thead className="bg-muted/60 text-xs uppercase tracking-wider text-muted-foreground">
                <tr><th className="text-left px-4 py-1.5">Typ</th><th className="text-left px-4 py-1.5">Von</th><th className="text-left px-4 py-1.5">Bis</th><th className="text-left px-4 py-1.5">Notiz</th></tr>
              </thead>
              <tbody>
                {leaves.map((l) => (
                  <tr key={l.id} className="border-t border-border">
                    <td className="px-4 py-1.5">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${l.type === "urlaub" ? "bg-sky-100 text-sky-700 dark:bg-sky-900/60 dark:text-sky-200" : "bg-orange-100 text-orange-700 dark:bg-orange-900/60 dark:text-orange-200"}`}>
                        {l.type === "urlaub" ? <Plane className="h-3 w-3" /> : <Stethoscope className="h-3 w-3" />}
                        {l.type === "urlaub" ? "Urlaub" : "Krank"}
                      </span>
                    </td>
                    <td className="px-4 py-1.5 tabular-nums">{fmtDate(l.date_from)}</td>
                    <td className="px-4 py-1.5 tabular-nums">{fmtDate(l.date_to)}</td>
                    <td className="px-4 py-1.5 text-muted-foreground">{l.note || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}

        {/* Worktimes */}
        <Card className="overflow-x-auto">
          <div className="px-4 py-2 border-b border-border text-sm font-semibold heading">Arbeitszeiten</div>
          <table className="w-full text-sm">
            <thead className="bg-muted/60 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-1.5">Start</th>
                <th className="text-left px-4 py-1.5">Ende</th>
                <th className="text-left px-4 py-1.5">Fahrzeug</th>
                <th className="text-left px-4 py-1.5">Dauer</th>
                <th className="text-left px-4 py-1.5">Pause</th>
                <th className="text-left px-4 py-1.5">Fahrten</th>
              </tr>
            </thead>
            <tbody>
              {worktimes.map((w) => (
                <tr key={w.id} className="border-t border-border">
                  <td className="px-4 py-1.5 tabular-nums whitespace-nowrap">{fmtDateTime(w.start_time)}</td>
                  <td className="px-4 py-1.5 tabular-nums whitespace-nowrap">{w.end_time ? fmtDateTime(w.end_time) : <span className="text-emerald-600">läuft</span>}</td>
                  <td className="px-4 py-1.5 flex items-center gap-1"><Car className="h-3.5 w-3.5 text-muted-foreground" />{w.license_plate || w.vehicle_name || "—"}</td>
                  <td className="px-4 py-1.5 tabular-nums">{fmt(w.duration_minutes)}</td>
                  <td className="px-4 py-1.5 tabular-nums text-muted-foreground"><Coffee className="inline h-3 w-3 mr-0.5" />{fmt(w.break_minutes)}</td>
                  <td className="px-4 py-1.5 tabular-nums">{w.trip_count ?? 0}</td>
                </tr>
              ))}
              {worktimes.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-4 text-center text-sm text-muted-foreground">Keine Einträge.</td></tr>
              )}
            </tbody>
          </table>
        </Card>
      </DialogContent>
    </Dialog>
  );
}
